import { useState, useTransition } from "react";
import { useApi, apiCall } from "../../api/client";
import { dod as dodContract, type TaskDod } from "../../api/contracts/dod";
import { Alert, AlertDescription, AlertTitle } from "../../alert";
import { Button } from "../../button";
import { Card } from "../../card";
import { Checkbox } from "../../checkbox";
import { Skeleton } from "../../skeleton";
import { IntentBadge } from "../status-badge";
import { PeekDialogSection } from "../peek-dialog";
import { formatDateTime, formatRelativeTime } from "../../format";
import type { Intent } from "../../tokens";

/**
 * The task peek's Definition-of-Done panel (#44086 part 2).
 *
 * Two things live here, and they are one loop rather than two features:
 *
 *   THE CONTRACT — the criteria that gate `done`, each with the verdict, the
 *   proof string and the receipt the router actually recorded, plus the retry
 *   history that says what changed between attempts.
 *
 *   THE LABEL — the work-class the deriver assigned, the paths no rule claimed,
 *   and one tap to confirm or correct it. The correction is the human feedback
 *   seam the per-class proving program drives through; it writes a labeled eval
 *   case and a `dod:reclassified` decision record, and shows up in the per-class
 *   rollup right below.
 *
 * Owns its own `PeekDialogSection` so a task with no DoD and no classification
 * renders NOTHING rather than a heading over a void — and renders an explicit
 * UNKNOWN when the read itself failed, because "no DoD" and "we could not look"
 * are opposite facts.
 */
export function TaskDodSection({ taskId }: { taskId: number }) {
  const { data, isLoading, mutate } = useApi(dodContract.task, {
    params: { id: taskId },
  });
  const [correcting, setCorrecting] = useState(false);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  if (isLoading && !data) {
    return (
      <PeekDialogSection title="Definition of done">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </PeekDialogSection>
    );
  }

  if (!data) return null;

  if (!data.readable) {
    return (
      <PeekDialogSection title="Definition of done">
        <Alert>
          <AlertTitle>Treat this as UNKNOWN</AlertTitle>
          <AlertDescription>
            The DoD could not be read
            {data.unreadableReason ? ` — ${data.unreadableReason}` : ""}. This is
            not the same as a task with no criteria.
          </AlertDescription>
        </Alert>
      </PeekDialogSection>
    );
  }

  const hasCriteria = data.criteria.length > 0;
  if (!hasCriteria && !data.classification) return null;

  const derived = data.classification?.classes ?? [];
  const selection = picked ?? (data.label?.classes.length ? data.label.classes : derived);

  function submit(verdict: "confirmed" | "corrected", classes: string[]) {
    setError(null);
    startTransition(async () => {
      try {
        const res = await apiCall(dodContract.label, {
          params: { id: taskId },
          body: { verdict, classes },
        });
        if (!res.ok) setError(res.reason ?? "refused");
        else {
          setCorrecting(false);
          setPicked(null);
        }
        // Adopt SERVER TRUTH either way — the panel never renders its own guess
        // about what landed.
        await mutate(res.view, { revalidate: false });
      } catch (e) {
        setError((e as Error).message);
      }
    });
  }

  return (
    <PeekDialogSection title="Definition of done" count={data.bindingCount}>
      <WorkClassBlock
        data={data}
        derived={derived}
        selection={selection}
        correcting={correcting}
        pending={pending}
        onStartCorrecting={() => {
          setPicked(selection);
          setCorrecting(true);
        }}
        onCancel={() => {
          setCorrecting(false);
          setPicked(null);
        }}
        onToggle={(cls) =>
          setPicked((prev) => {
            const base = prev ?? selection;
            return base.includes(cls)
              ? base.filter((c) => c !== cls)
              : [...base, cls];
          })
        }
        onConfirm={() => submit("confirmed", derived.length ? derived : selection)}
        onSaveCorrection={() => submit("corrected", selection)}
      />

      {error ? (
        <div className="type-body text-muted-foreground">label: {error}</div>
      ) : null}

      {hasCriteria ? <CriteriaList data={data} /> : null}
      {data.retries.length > 0 ? <RetryList data={data} /> : null}
    </PeekDialogSection>
  );
}

// ---------------------------------------------------------------------------
// Work class + the one-tap labeling control
// ---------------------------------------------------------------------------

function WorkClassBlock({
  data,
  derived,
  selection,
  correcting,
  pending,
  onStartCorrecting,
  onCancel,
  onToggle,
  onConfirm,
  onSaveCorrection,
}: {
  data: TaskDod;
  derived: string[];
  selection: string[];
  correcting: boolean;
  pending: boolean;
  onStartCorrecting: () => void;
  onCancel: () => void;
  onToggle: (cls: string) => void;
  onConfirm: () => void;
  onSaveCorrection: () => void;
}) {
  const c = data.classification;
  const metricFor = (cls: string) => data.classMetrics.find((m) => m.cls === cls);

  return (
    <Card className="flex flex-col gap-3 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2 type-body">
        <span className="text-muted-foreground">Work class</span>
        {derived.length === 0 ? (
          <span className="text-muted-foreground">not derived yet</span>
        ) : (
          derived.map((cls) => (
            <IntentBadge key={cls} intent={cls === "unclassified" ? "warning" : "info"}>
              {cls}
            </IntentBadge>
          ))
        )}
        {c && c.unclassified > 0 ? (
          <IntentBadge intent="warning">
            {c.unclassified} unclassified path{c.unclassified === 1 ? "" : "s"}
          </IntentBadge>
        ) : null}
      </div>

      {c?.floorApplied ? (
        <div className="type-body text-muted-foreground">
          No rule claimed any path — the FLOOR was applied. That is the minimum
          any change owes, not a judgement that this work is low-risk.
        </div>
      ) : null}

      {c && c.unclassifiedSample.length > 0 ? (
        <ul className="flex flex-col gap-0.5 type-body text-muted-foreground">
          {c.unclassifiedSample.map((p) => (
            <li key={p}>? {p}</li>
          ))}
        </ul>
      ) : null}

      {data.label ? (
        <div className="type-body text-muted-foreground">
          {data.label.verdict === "confirmed" ? "Confirmed" : "Corrected"} to{" "}
          {data.label.classes.join(" + ")} {formatRelativeTime(data.label.at)}
          {data.label.by ? ` by ${data.label.by}` : ""}
        </div>
      ) : null}

      {correcting ? (
        <div className="flex flex-col gap-2">
          {data.workClasses.map((cls) => {
            const m = metricFor(cls);
            return (
              <label
                key={cls}
                className="flex items-center gap-2 border-b border-border/40 py-1.5 type-body last:border-b-0"
              >
                <Checkbox
                  checked={selection.includes(cls)}
                  onCheckedChange={() => onToggle(cls)}
                  disabled={pending}
                />
                <span className="flex-1 text-foreground">{cls}</span>
                {m ? (
                  <span className="type-body text-muted-foreground">
                    {m.derivedPaths} derived · {m.confirmed} confirmed ·{" "}
                    {m.corrected} corrected
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {correcting ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={onSaveCorrection}
              disabled={pending || selection.length === 0}
            >
              Save correction
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onCancel}
              disabled={pending}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={onConfirm}
              disabled={pending || !data.classification}
            >
              Confirm
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onStartCorrecting}
              disabled={pending || !data.classification}
            >
              Correct…
            </Button>
          </>
        )}
        {c ? (
          <span className="type-body text-muted-foreground">
            derived {formatRelativeTime(c.at)} · registry {c.registrySha}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The criteria themselves
// ---------------------------------------------------------------------------

function verdictBadge(pass: boolean | null): { label: string; intent: Intent } {
  if (pass === true) return { label: "met", intent: "success" };
  if (pass === false) return { label: "unmet", intent: "danger" };
  return { label: "not evaluated", intent: "neutral" };
}

function CriteriaList({ data }: { data: TaskDod }) {
  return (
    <Card className="px-4 py-2">
      {data.criteria.map((crit) => {
        const v = verdictBadge(crit.pass);
        return (
          <div
            key={crit.id}
            className="border-b border-border/40 py-2 type-body first:pt-0 last:border-b-0"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-1 text-foreground">
                {crit.ref ?? `criterion #${crit.id}`}
              </span>
              <IntentBadge intent={v.intent}>{v.label}</IntentBadge>
              <IntentBadge intent={crit.binding ? "info" : "neutral"}>
                {crit.binding ? "gates done" : "proposal"}
              </IntentBadge>
            </div>
            {crit.why ? (
              <div className="mt-1 text-muted-foreground">{crit.why}</div>
            ) : null}
            {crit.proof ? (
              <div className="mt-1 whitespace-pre-wrap break-words text-muted-foreground">
                {crit.proof}
              </div>
            ) : null}
            <div className="mt-1 flex flex-wrap gap-x-3 text-muted-foreground">
              <span>{crit.source}</span>
              {crit.attempt != null ? <span>attempt {crit.attempt}</span> : null}
              {crit.evaluatedAt ? (
                <span>evaluated {formatDateTime(crit.evaluatedAt)}</span>
              ) : null}
              {crit.receipt ? (
                <span>
                  receipt exit {crit.receipt.exitCode} · {crit.receipt.binding}
                  {crit.receipt.mode ? ` · ${crit.receipt.mode}` : ""}
                  {crit.receipt.mode === "delta" &&
                  crit.receipt.baseCount != null &&
                  crit.receipt.headCount != null
                    ? ` ${crit.receipt.baseCount}→${crit.receipt.headCount}`
                    : ""}{" "}
                  · {formatDateTime(crit.receipt.ranAt)}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Retry history
// ---------------------------------------------------------------------------

function RetryList({ data }: { data: TaskDod }) {
  return (
    <Card className="px-4 py-2">
      {data.retries.map((r) => (
        <div
          key={`${r.attempt}-${r.at}`}
          className="border-b border-border/40 py-2 type-body first:pt-0 last:border-b-0"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex-1 text-foreground">Retry #{r.attempt}</span>
            <IntentBadge intent={r.pass ? "success" : "warning"}>
              {r.pass ? "passed" : "still unmet"}
            </IntentBadge>
            <span className="text-muted-foreground">{formatDateTime(r.at)}</span>
          </div>
          <div className="mt-1 text-muted-foreground">
            {r.fixed.length} fixed · {r.stillFailing.length} still failing ·{" "}
            {r.newlyFailing.length} newly failing
          </div>
          {r.newlyFailing.length > 0 ? (
            <div className="mt-1 text-muted-foreground">
              newly failing: {r.newlyFailing.join(", ")}
            </div>
          ) : null}
        </div>
      ))}
    </Card>
  );
}
